/**
 * Page de suivi de commande — interroge `POST /api/suivi` sur le Worker et dessine la frise.
 *
 * TROIS PARTIS PRIS.
 *
 * 1. TOUT EST CONSTRUIT PAR `textContent`, jamais par `innerHTML` sur une donnée venue du
 *    serveur. La réponse contient un numéro de commande, un numéro de suivi et un lien, donc
 *    des valeurs qui transitent par des systèmes tiers. Concaténer du HTML ici serait une
 *    injection à retardement, et la page est publique.
 *
 * 2. LES DATES SONT DÉCOUPÉES À LA MAIN plutôt que passées à `new Date('2026-09-20')`. Cette
 *    forme est interprétée en UTC : affichée dans un fuseau en retard sur Greenwich, elle
 *    recule d'un jour. Une frise qui affiche la veille de la vraie date sur une page de suivi,
 *    c'est précisément le genre d'écart qui fait écrire au support.
 *
 * 3. LES TEXTES FRANÇAIS SONT CEUX D'AVANT, ICI MÊME ; UNE AUTRE LANGUE LES REMPLACE PAR UN
 *    `<template data-sd-suivi-textes>`, posé par la section avec les clés `securidou.suivi.js.*`
 *    du châssis i18n, et demande sa langue au Worker par `data-langue`. Sans ces attributs, la
 *    requête et le rendu sont ceux d'avant, à l'octet : `scripts/test/suivi-commande-langue.test.mjs`
 *    fait tourner l'ancien script et celui-ci sur les mêmes réponses. Seul écart admis depuis la refonte du
 *    19/10/2026 (spec 2026-10-19-refonte-pages-aide-suivi-design.md) : une fenêtre dépassée ne montre plus de
 *    date passée ; le test nomme les trois textes qui changent.
 */
(function () {
  'use strict';

  var racine = document.querySelector('[data-sd-suivi]');
  if (!racine) return;

  var form = racine.querySelector('[data-sd-suivi-form]');
  var bouton = racine.querySelector('[data-sd-suivi-submit]');
  var sortie = racine.querySelector('[data-sd-suivi-result]');
  var champCommande = racine.querySelector('#sd-suivi-commande');
  var champEmail = racine.querySelector('#sd-suivi-email');
  var endpoint = racine.getAttribute('data-endpoint') || '';
  // Langue demandée au Worker (`fr` ou `en`). Absente, il rend la réponse d'avant, à l'octet.
  var langue = racine.getAttribute('data-langue');

  // Les valeurs françaises de `securidou.suivi.js.*` (locales/fr.json) sont ces textes, mot pour mot.
  var T = {
    mois: 'janvier,février,mars,avril,mai,juin,juillet,août,septembre,octobre,novembre,décembre',
    premier: '1er',
    commande: 'Commande {numero}',
    livre_titre: 'Votre colis est arrivé',
    livre_le: 'Livré le {date}',
    livre: 'Livré',
    anomalie_titre: 'Une difficulté est signalée sur votre colis',
    anomalie_valeur: 'Écrivez-nous, nous le prenons en charge',
    estimee_echantillon: 'Livraison estimée, d’après nos {n} dernières livraisons',
    estimee: 'Livraison estimée',
    estimee_jour: 'Le {date}',
    estimee_fenetre: 'Entre le {debut} et le {fin}',
    expedie_titre: 'Votre colis est en route',
    expedie_le: 'Expédié le {date}',
    expedie: 'Expédié',
    relais_titre: 'Votre colis vous attend en point relais',
    relais_depuis: 'Depuis le {date}',
    commande_titre: 'Votre commande',
    preparation: 'En cours de préparation',
    acheminement: 'Acheminement en cours',
    acheminement_aide: 'Les étapes suivantes apparaîtront dès que le transporteur nous les transmet.',
    recours_anomalie: 'Une difficulté est signalée sur votre colis.',
    recours_depasse: 'Votre colis prend plus de temps que prévu.',
    recours_contact:
      'Écrivez-nous à {email} en indiquant votre numéro de commande. Nous ouvrons une recherche auprès du transporteur et nous vous répondons sous 24 heures. Si le colis est perdu, nous vous remboursons intégralement.',
    derniere_info: 'Dernière information reçue le {date}.',
    numero_suivi: 'Numéro de suivi : {numero}',
    detail_transporteur: 'Détail transporteur, étape par étape, sur {lien}.',
    lien_17track: 'https://t.17track.net/fr#nums=',
    confirm_question: 'Avez-vous reçu votre colis ?',
    confirm_aide:
      'Votre réponse nous sert à afficher des délais justes aux prochains clients, et à agir tout de suite si votre colis tarde.',
    confirm_oui: 'Oui, je l’ai reçu',
    confirm_non: 'Non, pas encore',
    confirm_echec:
      'Nous n’avons pas pu enregistrer votre réponse. Écrivez-nous à contact@securidou.fr, nous prenons le relais.',
    confirm_merci: 'Merci, c’est noté. Bonne découverte de votre commande.',
    confirm_ticket:
      'C’est noté. Nous ouvrons une recherche auprès du transporteur et nous revenons vers vous sous 24 heures',
    confirm_reference: ', référence {ref}.',
    confirm_connexion:
      'La connexion a échoué. Écrivez-nous à contact@securidou.fr, nous prenons le relais.',
    recu_le: 'Reçu le',
    valider: 'Valider',
    err_champs: 'Merci d’indiquer votre numéro de commande et l’e-mail utilisé lors de l’achat.',
    err_indisponible:
      'Le suivi en ligne n’est pas disponible pour le moment. Écrivez-nous à contact@securidou.fr avec votre numéro de commande, nous vous répondons sous 24 heures.',
    recherche: 'Recherche en cours...',
    err_trop:
      'Trop de recherches depuis cet appareil. Patientez une heure, ou écrivez-nous à contact@securidou.fr.',
    err_format:
      'Le numéro de commande ou l’e-mail n’est pas au bon format. Le numéro ressemble à 1022.',
    err_serveur:
      'Le suivi est momentanément indisponible. Réessayez dans quelques minutes, ou écrivez-nous à contact@securidou.fr.',
    err_connexion: 'La connexion au suivi a échoué. Vérifiez votre connexion, puis réessayez.'
  };

  /**
   * Numéro de commande pré-rempli depuis le lien de l'e-mail d'expédition (`#commande=1028`).
   * Le fragment ne quitte jamais le navigateur : ni serveur ni journal ne le voit. Il ne porte que
   * le numéro, jamais l'e-mail, qui reste le second facteur que la personne saisit elle-même.
   */
  function preRemplir() {
    if (typeof window === 'undefined' || !window.location) return;
    var m = /(?:^#|&)commande=(\d{3,12})(?:&|$)/.exec(window.location.hash || '');
    if (!m || champCommande.value) return;
    champCommande.value = m[1];
    if (typeof champEmail.focus === 'function') champEmail.focus();
  }
  preRemplir();

  var gabarit = racine.querySelector('template[data-sd-suivi-textes]');
  if (gabarit) {
    var traduits = gabarit.content.querySelectorAll('[data-cle]');
    for (var t = 0; t < traduits.length; t++) {
      T[traduits[t].getAttribute('data-cle')] = traduits[t].textContent;
    }
  }

  var MOIS = T.mois.split(',');

  /**
   * Remplace les jetons `{nom}` d'un texte. Une fonction, pas une chaîne : un `$` d'une valeur reste un `$`.
   * Chaque valeur ne porte que les jetons de sa clé, une fois chacun (test des jetons, trois locales).
   */
  function remplir(modele, valeurs) {
    return modele.replace(/\{(\w+)\}/g, function (tout, nom) {
      return String(valeurs[nom]);
    });
  }

  /** Ajoute à `parent` le texte de `modele`, avec `noeud` à la place du jeton : un lien reste un nœud, jamais du HTML. */
  function autour(parent, modele, jeton, noeud) {
    var parts = modele.split(jeton);
    parent.appendChild(document.createTextNode(parts[0]));
    parent.appendChild(noeud);
    // Pas de nœud vide en fin de phrase : le pied d'avant n'en avait pas.
    if (parts[1]) parent.appendChild(document.createTextNode(parts[1]));
  }

  function formaterJour(iso) {
    if (typeof iso !== 'string') return '';
    var p = iso.split('-');
    if (p.length !== 3) return '';
    var jour = parseInt(p[2], 10);
    var mois = parseInt(p[1], 10) - 1;
    if (isNaN(jour) || isNaN(mois) || !MOIS[mois]) return '';
    var texte = (jour === 1 ? T.premier : String(jour)) + ' ' + MOIS[mois];
    var annee = parseInt(p[0], 10);
    if (annee !== new Date().getFullYear()) texte += ' ' + annee;
    return texte;
  }

  function creer(balise, classe, texte) {
    var el = document.createElement(balise);
    if (classe) el.className = classe;
    if (texte) el.textContent = texte;
    return el;
  }

  function vider() {
    while (sortie.firstChild) sortie.removeChild(sortie.firstChild);
  }

  // Emplacement sous le bouton (refonte du 19/10) ; absent, l'erreur reste dans le résultat, comme avant.
  var fenteErreur = racine.querySelector('[data-sd-suivi-erreur]');

  function viderErreur() {
    if (!fenteErreur) return;
    while (fenteErreur.firstChild) fenteErreur.removeChild(fenteErreur.firstChild);
  }

  function afficherErreur(message) {
    vider();
    var bloc = creer('div', 'sd-suivi__error', message);
    if (fenteErreur) {
      viderErreur();
      fenteErreur.appendChild(bloc);
      sortie.hidden = true;
      return;
    }
    sortie.appendChild(bloc);
    sortie.hidden = false;
  }

  /**
   * Sous 990 px, le panneau du résultat s'ouvre sous le formulaire, souvent hors de l'écran : la page y descend.
   * Sans glissement si le mouvement est réduit ; rien hors d'un navigateur (banc de test, sans `window`).
   */
  function defilerVersResultat() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    var cible = (sortie.closest && sortie.closest('.sv-panneau')) || sortie;
    if (typeof cible.scrollIntoView !== 'function') return;
    var reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    cible.scrollIntoView({ behavior: reduit ? 'auto' : 'smooth', block: 'start' });
  }

  function etapeDe(resume, code) {
    for (var i = 0; i < resume.etapes.length; i++) {
      if (resume.etapes[i].code === code) return resume.etapes[i];
    }
    return null;
  }

  function construireEta(donnees) {
    var resume = donnees.resume;
    var echantillon = donnees.estimation_echantillon || 0;
    var bloc = creer('div', 'sd-suivi__eta');
    var label = creer('span', 'sd-suivi__eta-label');
    var valeur = creer('span', 'sd-suivi__eta-value');

    if (resume.livre) {
      bloc.className += ' sd-suivi__eta--done';
      var etapeLivre = etapeDe(resume, 'livre');
      label.textContent = T.livre_titre;
      valeur.textContent = etapeLivre && etapeLivre.date
        ? remplir(T.livre_le, { date: formaterJour(etapeLivre.date) })
        : T.livre;
    } else if (resume.anomalie) {
      // Pas d'estimation dans ce cas : le serveur la retire, et la page ne doit pas en
      // fabriquer une. Une date annoncée sur un colis en difficulté est une promesse de plus.
      bloc.className += ' sd-suivi__eta--alert';
      label.textContent = T.anomalie_titre;
      valeur.textContent = T.anomalie_valeur;
    } else if (resume.point_relais === true) {
      // Le colis est arrivé et attend en point relais : il n'y a plus rien à estimer, et une date
      // de livraison annoncée laisserait la personne attendre chez elle un colis à aller chercher.
      var etapeRelais = etapeDe(resume, 'livraison');
      bloc.className += ' sd-suivi__eta--done';
      label.textContent = T.relais_titre;
      valeur.textContent = etapeRelais && etapeRelais.date
        ? remplir(T.relais_depuis, { date: formaterJour(etapeRelais.date) })
        : etapeRelais ? etapeRelais.libelle : '';
    } else if (resume.livraison_estimee && aujourdhui() > resume.livraison_estimee.fin) {
      // Fenêtre dépassée : aucune date passée n'est affichée (décision de l'exploitant du 19/10). La page dit ce qui
      // est sûr, comme hors de France ; l'encart de recours, plus bas, donne le chemin.
      var etapeDepart = etapeDe(resume, 'prise_en_charge');
      if (resume.etape_courante === 'confirmee') {
        label.textContent = T.commande_titre;
        valeur.textContent = T.preparation;
      } else {
        label.textContent = T.expedie_titre;
        valeur.textContent = etapeDepart && etapeDepart.date
          ? remplir(T.expedie_le, { date: formaterJour(etapeDepart.date) })
          : T.expedie;
      }
    } else if (resume.livraison_estimee) {
      var debut = formaterJour(resume.livraison_estimee.debut);
      var fin = formaterJour(resume.livraison_estimee.fin);
      // La formulation dépend de l'échantillon, et c'est une question d'honnêteté, pas de style :
      // « d'après nos N dernières livraisons » ne s'écrit que si N livraisons réelles ont servi au
      // calcul. Sinon la fenêtre vient d'une constante et la page ne le maquille pas.
      label.textContent =
        echantillon >= 5
          ? remplir(T.estimee_echantillon, { n: echantillon })
          : T.estimee;
      valeur.textContent = debut === fin
        ? remplir(T.estimee_jour, { date: debut })
        : remplir(T.estimee_fenetre, { debut: debut, fin: fin });
    } else if (donnees.hors_france === true && resume.etape_courante !== 'confirmee') {
      // Hors de France, le Worker ne chiffre aucun délai (décision D1 du 23/09) : sans cette
      // branche, un colis parti s'afficherait « En cours de préparation ». La page dit ce qui
      // est sûr, la date d'expédition, et rien de plus. `hors_france` n'arrive qu'avec `langue`.
      var etapePrise = etapeDe(resume, 'prise_en_charge');
      label.textContent = T.expedie_titre;
      valeur.textContent = etapePrise && etapePrise.date
        ? remplir(T.expedie_le, { date: formaterJour(etapePrise.date) })
        : T.expedie;
    } else {
      label.textContent = T.commande_titre;
      valeur.textContent = T.preparation;
    }

    bloc.appendChild(label);
    bloc.appendChild(valeur);
    return bloc;
  }

  /**
   * Construit la frise.
   *
   * LE POINT DÉLICAT est le cas sans données transporteur. Afficher « En transit » et « Arrivé en
   * France » en gris se lit comme « ce n'est pas arrivé », alors que le colis est bel et bien
   * parti : c'est une négation que rien ne soutient. On n'affiche donc que les étapes réellement
   * connues, suivies d'une ligne d'attente qui dit ce qu'il en est. Mieux vaut montrer moins que
   * suggérer faux.
   */
  function construireFrise(donnees) {
    var resume = donnees.resume;
    var liste = creer('ul', 'sd-suivi__steps');
    var partiel = !donnees.suivi_detaille && !resume.livre;

    for (var i = 0; i < resume.etapes.length; i++) {
      var etape = resume.etapes[i];
      if (partiel && !etape.atteinte) continue;

      var classe = 'sd-suivi__step';
      if (etape.atteinte) classe += ' sd-suivi__step--done';
      if (etape.code === resume.etape_courante && !resume.livre) {
        classe += ' sd-suivi__step--current';
      }
      var item = creer('li', classe);
      item.appendChild(creer('span', 'sd-suivi__step-label', etape.libelle));
      if (etape.date) {
        item.appendChild(creer('span', 'sd-suivi__step-date', formaterJour(etape.date)));
      }
      liste.appendChild(item);
    }

    if (partiel) {
      var attente = creer('li', 'sd-suivi__step sd-suivi__step--attente');
      attente.appendChild(creer('span', 'sd-suivi__step-label', T.acheminement));
      attente.appendChild(creer('span', 'sd-suivi__step-date', T.acheminement_aide));
      liste.appendChild(attente);
    }

    return liste;
  }

  /**
   * Date de la dernière étape réellement connue. Ce n'est pas un détail de confort : afficher
   * « dernière information reçue le 12 septembre » dit honnêtement au client jusqu'où va notre
   * connaissance, au lieu de le laisser interpréter des pastilles grises comme « rien ne bouge ».
   * Une information datée se conteste beaucoup moins qu'un silence.
   */
  function derniereInfo(resume) {
    var date = null;
    for (var i = 0; i < resume.etapes.length; i++) {
      if (resume.etapes[i].atteinte && resume.etapes[i].date) date = resume.etapes[i].date;
    }
    return date;
  }

  /** Aujourd'hui au format AAAA-MM-JJ, en heure locale, pour comparer à une date d'étape. */
  function aujourdhui() {
    var d = new Date();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var j = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + j;
  }

  /**
   * Encart de recours, affiché seulement quand la fenêtre estimée est dépassée ou qu'une
   * anomalie est signalée.
   *
   * C'est la pièce anti-litige de la page. Un client qui voit une date dépassée sans savoir quoi
   * faire appelle sa banque ; un client à qui on dit, sur la page même, quoi faire et sous quel
   * délai, nous écrit. On n'invente aucune date et on ne minimise rien : on nomme le dépassement
   * et on donne le chemin.
   */
  function construireRecours(resume) {
    var depasse =
      resume.livraison_estimee && aujourdhui() > resume.livraison_estimee.fin && !resume.livre;
    if (!depasse && !resume.anomalie) return null;

    var bloc = creer('div', 'sd-suivi__recours');
    bloc.appendChild(
      creer('strong', null, resume.anomalie ? T.recours_anomalie : T.recours_depasse)
    );
    var p = creer('p', null);
    var a = creer('a', null, 'contact@securidou.fr');
    a.href = 'mailto:contact@securidou.fr';
    // Tournure sans accord de participe : « remboursé(e) » présumerait le genre de la personne
    // qui lit, et cette page est vue par des parents, pas seulement des mères.
    autour(p, T.recours_contact, '{email}', a);
    bloc.appendChild(p);
    return bloc;
  }

  function construirePied(donnees) {
    var pied = creer('p', 'sd-suivi__foot');

    var maj = derniereInfo(donnees.resume);
    if (maj) {
      pied.appendChild(creer('span', null, remplir(T.derniere_info, { date: formaterJour(maj) })));
      pied.appendChild(document.createElement('br'));
    }

    if (donnees.numero_suivi) {
      autour(pied, T.numero_suivi, '{numero}', creer('strong', null, donnees.numero_suivi));
    }

    // LIEN 17TRACK, REMIS LE 21/09/2026. TEMPORAIRE PAR CONSTRUCTION, LIRE AVANT DE TOUCHER.
    //
    // Il avait été retiré parce qu'un client s'était plaint d'être renvoyé sur un site tiers, et
    // cette plainte reste valable. Ce qui a changé est plus coûteux qu'elle : sans clé AfterShip,
    // la frise ci-dessus s'arrête à l'expédition, donc elle affiche « pas encore livré » sur un
    // colis que le transporteur a remis. Sur la commande #1023, livrée le 18/09, elle l'affichait
    // encore le 21 au matin, quand la cliente a ouvert une rétrofacturation « produit non reçu ».
    // Une page qui nie une livraison coûte plus cher qu'un renvoi vers un agrégateur.
    //
    // LA CONDITION EST LE CORRECTIF. `!suivi_detaille` veut dire « aucun événement transporteur
    // n'est arrivé jusqu'ici ». Le jour où AfterShip est provisionné, la frise devient complète,
    // ce lien disparaît de lui-même et la page redevient ce qu'elle était, sans qu'il faille y
    // repenser. C'est un pansement qui tombe tout seul, pas une décision à défaire plus tard.
    //
    // Sur l'origine du produit, visible sur 17track sous la forme « Shenzhen » : la montrer est
    // le côté sûr de L121-2 2° b, pas le côté risqué. Ce n'est pas une raison de retirer ce lien.
    if (donnees.numero_suivi && !donnees.suivi_detaille) {
      if (pied.childNodes.length) pied.appendChild(document.createElement('br'));
      var lien = creer('a', null, '17track');
      lien.href = T.lien_17track + encodeURIComponent(donnees.numero_suivi);
      lien.target = '_blank';
      lien.rel = 'noopener noreferrer';
      autour(pied, T.detail_transporteur, '{lien}', lien);
    }

    // Pas de message d'attente ici : la frise porte déjà son étape « Acheminement en cours ».
    // Le dire deux fois sur le même écran donne l'impression d'une page qui se justifie.

    return pied.childNodes.length ? pied : null;
  }

  /**
   * Question de confirmation, posée seulement passé la fenêtre estimée.
   *
   * POURQUOI ELLE EXISTE. Shopify connaît la date de commande et celle d'expédition, jamais celle
   * de réception. Aucune date de livraison réelle n'existait donc dans le système : l'estimation
   * était une supposition et personne ne pouvait dire si l'acheminement s'améliorait. La seule
   * partie qui connaît toujours cette date est la personne qui reçoit le colis.
   *
   * La réponse « pas encore » compte autant que l'autre : elle ouvre un ticket, donc elle signale
   * une cliente à rappeler avant qu'elle ne s'énerve. C'est exactement ce qui a manqué sur la
   * commande #1016, dont la cliente a relancé quatre fois sans réponse.
   */
  function construireConfirmation(donnees, commande, email) {
    var resume = donnees.resume;
    var pertinent =
      !resume.livre && resume.livraison_estimee && aujourdhui() > resume.livraison_estimee.debut;
    if (!pertinent) return null;

    var bloc = creer('div', 'sd-suivi__confirm');
    bloc.appendChild(creer('strong', null, T.confirm_question));
    bloc.appendChild(creer('p', 'sd-suivi__confirm-aide', T.confirm_aide));

    var actions = creer('div', 'sd-suivi__confirm-actions');
    var oui = creer('button', 'sd-suivi__confirm-btn', T.confirm_oui);
    oui.type = 'button';
    var non = creer('button', 'sd-suivi__confirm-btn sd-suivi__confirm-btn--secondaire', T.confirm_non);
    non.type = 'button';
    actions.appendChild(oui);
    actions.appendChild(non);
    bloc.appendChild(actions);

    var retour = creer('p', 'sd-suivi__confirm-retour');
    retour.hidden = true;
    bloc.appendChild(retour);

    function envoyer(recu, recuLe) {
      oui.disabled = true;
      non.disabled = true;
      return fetch(endpoint + '/confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commande: commande, email: email, recu: recu, recu_le: recuLe })
      })
        .then(function (r) {
          return r.json().catch(function () {
            return {};
          });
        })
        .then(function (corps) {
          actions.hidden = true;
          retour.hidden = false;
          if (!corps || corps.ok !== true) {
            retour.textContent = T.confirm_echec;
            return;
          }
          retour.textContent = recu
            ? T.confirm_merci
            : T.confirm_ticket +
              (corps.reference ? remplir(T.confirm_reference, { ref: corps.reference }) : '.');
        })
        .catch(function () {
          actions.hidden = true;
          retour.hidden = false;
          retour.textContent = T.confirm_connexion;
        });
    }

    // « Oui » demande la date plutôt que de supposer aujourd'hui : quelqu'un qui répond trois
    // jours après réception fausserait la moyenne vers le haut, et c'est cette moyenne qui sert
    // ensuite à annoncer un délai aux autres.
    oui.addEventListener('click', function () {
      actions.hidden = true;
      var boite = creer('div', 'sd-suivi__confirm-date');
      var label = creer('label', 'sd-suivi__label', T.recu_le);
      label.setAttribute('for', 'sd-suivi-recu-le');
      var champ = document.createElement('input');
      champ.type = 'date';
      champ.id = 'sd-suivi-recu-le';
      champ.className = 'sd-suivi__input';
      champ.value = aujourdhui();
      champ.max = aujourdhui();
      var valider = creer('button', 'sd-suivi__confirm-btn', T.valider);
      valider.type = 'button';
      valider.addEventListener('click', function () {
        envoyer(true, champ.value || aujourdhui());
      });
      boite.appendChild(label);
      boite.appendChild(champ);
      boite.appendChild(valider);
      bloc.insertBefore(boite, retour);
    });

    non.addEventListener('click', function () {
      envoyer(false, null);
    });

    return bloc;
  }

  function afficher(donnees, commande, email) {
    vider();
    sortie.appendChild(creer('p', 'sd-suivi__order', remplir(T.commande, { numero: donnees.commande })));
    sortie.appendChild(construireEta(donnees));
    sortie.appendChild(construireFrise(donnees));
    var recours = construireRecours(donnees.resume);
    if (recours) sortie.appendChild(recours);
    var confirmation = construireConfirmation(donnees, commande, email);
    if (confirmation) sortie.appendChild(confirmation);
    var pied = construirePied(donnees);
    if (pied) sortie.appendChild(pied);
    sortie.hidden = false;
    defilerVersResultat();
  }

  function marquerInvalide(champ, invalide) {
    if (invalide) champ.setAttribute('aria-invalid', 'true');
    else champ.removeAttribute('aria-invalid');
  }

  form.addEventListener('submit', function (evenement) {
    evenement.preventDefault();
    viderErreur();

    var commande = champCommande.value.trim();
    var email = champEmail.value.trim();

    marquerInvalide(champCommande, !commande);
    marquerInvalide(champEmail, email.indexOf('@') === -1);
    if (!commande || email.indexOf('@') === -1) {
      afficherErreur(T.err_champs);
      return;
    }

    if (!endpoint) {
      // Le réglage d'URL du Worker est vide ou mal formé. On le dit plutôt que de laisser la
      // page tourner dans le vide : c'est l'état exact du site tant que le Worker n'est pas
      // déployé, et il ne doit pas ressembler à une panne côté client.
      afficherErreur(T.err_indisponible);
      return;
    }

    bouton.disabled = true;
    var libelleInitial = bouton.textContent;
    bouton.textContent = T.recherche;

    var corps = { commande: commande, email: email };
    if (langue) corps.langue = langue;

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps)
    })
      .then(function (reponse) {
        return reponse
          .json()
          .catch(function () {
            return {};
          })
          .then(function (corps) {
            return { statut: reponse.status, corps: corps };
          });
      })
      .then(function (resultat) {
        if (resultat.statut === 200 && resultat.corps && resultat.corps.ok) {
          afficher(resultat.corps, commande, email);
          return;
        }
        if (resultat.statut === 429) {
          afficherErreur(T.err_trop);
          return;
        }
        if (resultat.statut === 404 && resultat.corps && resultat.corps.message) {
          afficherErreur(resultat.corps.message);
          return;
        }
        if (resultat.statut === 400) {
          afficherErreur(T.err_format);
          return;
        }
        afficherErreur(T.err_serveur);
      })
      .catch(function () {
        afficherErreur(T.err_connexion);
      })
      .then(function () {
        bouton.disabled = false;
        bouton.textContent = libelleInitial;
      });
  });
})();
